import { Link } from "react-router-dom";
import { WarningCircle } from "@phosphor-icons/react";
import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { BUCKET_TONE, cr, pct, useWealthPosition } from "@/lib/wealth";
import { EmptyState } from "@/components/shared";

const Card = ({ label, value, sub, testid }) => (
  <div className="panel p-5" data-testid={testid}>
    <p className="label-caps">{label}</p>
    <p className="mt-2 font-mono text-2xl font-semibold tabular-nums text-navy">{value}</p>
    {sub && <p className="mt-1 text-[11px] font-medium text-muted-foreground">{sub}</p>}
  </div>
);

export default function WealthOverview() {
  const { position } = useWealthPosition();
  const t = position?.totals;

  return (
    <WealthShell
      title="Consolidated family wealth"
      description="Current position, calculated from the active asset and liability records. Values are stored in rupees and shown in ₹ crore."
    >
      {!t ? (
        <EmptyState testid="wealth-position-empty" title="No wealth records yet"
                    message="Add ownership entities, assets and liabilities in the register to build the current position." />
      ) : (
        <div className="space-y-6" data-testid="wealth-overview">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Card testid="wealth-card-net-worth" label="Total family net worth" value={cr(t.net_worth)}
                  sub={`${t.asset_count} active assets`} />
            <Card testid="wealth-card-gross" label="Gross assets" value={cr(t.gross_assets)} />
            <Card testid="wealth-card-liabilities" label="Total liabilities" value={cr(t.total_liabilities)}
                  sub={`Asset-linked ${cr(t.asset_linked_liabilities)} · standalone ${cr(t.standalone_liabilities)}`} />
            <Card testid="wealth-card-accessible" label="Accessible ≤ 90 days"
                  value={cr(position.accessible_90_days.value)}
                  sub={`${pct(position.accessible_90_days.pct)} of net worth`} />
          </div>

          {position.test_values?.length > 0 && (
            <div className="flex items-start gap-3 rounded-2xl border-l-4 border-warning bg-warning/10 px-5 py-4"
                 data-testid="wealth-test-value-note">
              <WarningCircle size={20} className="mt-0.5 shrink-0 text-[hsl(28_82%_38%)]" />
              <p className="text-sm text-[hsl(28_82%_30%)]">
                <span className="font-semibold">Controlled test value in use: </span>
                {position.test_values.map((a) => `${a.name} (${cr(a.net_value)})`).join(", ")} — the
                Business Value Engine (Phase 3) will replace this with a Panchatanthra-linked valuation.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {position.buckets.map((b) => {
              const tone = BUCKET_TONE[b.bucket];
              return (
                <Link key={b.bucket} to="/viveka-kartha/wealth/allocator"
                      data-testid={`wealth-bucket-${b.bucket}`}
                      className={`panel border-t-4 p-6 transition-transform hover:-translate-y-0.5 ${tone.border}`}>
                  <p className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${tone.chip}`}>
                    {b.label}
                  </p>
                  <p className="mt-4 font-mono text-3xl font-semibold tabular-nums text-navy">{pct(b.current_pct)}</p>
                  <p className="mt-1 font-mono text-sm text-foreground/80">{cr(b.value)}</p>
                  <p className="mt-3 text-xs text-muted-foreground">{b.intent}</p>
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${b.current_pct || 0}%` }} />
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="panel overflow-hidden" data-testid="wealth-liquidity-summary">
            <p className="border-b border-border px-6 py-4 font-display text-lg text-navy">Liquidity summary</p>
            <div className="divide-y divide-border">
              {position.liquidity.map((li) => (
                <div key={li.key} className="flex items-center gap-4 px-6 py-3" data-testid={`wealth-liq-${li.key}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-navy">{li.label}</p>
                    <p className="text-[11px] text-muted-foreground">{li.definition}</p>
                  </div>
                  <p className="w-32 shrink-0 text-right font-mono text-sm tabular-nums text-navy">{cr(li.value)}</p>
                  <p className="w-20 shrink-0 text-right font-mono text-sm tabular-nums text-foreground/70">{pct(li.pct)}</p>
                </div>
              ))}
            </div>
            <p className="px-6 py-3 text-[11px] text-muted-foreground">
              Configuration in use: asset classes {position.config_versions?.asset_classes} · liquidity bands{" "}
              {position.config_versions?.liquidity_bands}
            </p>
          </div>
        </div>
      )}
    </WealthShell>
  );
}
